import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { CaretRight, MagnifyingGlass, X } from "@phosphor-icons/react";

const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ras Al Khaimah", "Umm Al Quwain", "Ajman", "Fujairah"];

// Handover Q/Y strings we know how to sort
function yearOf(handover) {
  const m = String(handover || "").match(/(20\d{2})/);
  return m ? parseInt(m[1], 10) : null;
}

export default function Projects() {
  const [items, setItems] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState("");
  const [emirate, setEmirate] = useState("");
  const [community, setCommunity] = useState("");
  const [developerId, setDeveloperId] = useState("");
  const [handoverYear, setHandoverYear] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/projects"),
      api.get("/developers"),
    ]).then(([p, d]) => {
      setItems(p.data.items || []);
      setDevelopers(d.data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  const communityOptions = useMemo(() => {
    const set = new Set(items.map((p) => p.community).filter(Boolean));
    return [...set].sort();
  }, [items]);

  const handoverYears = useMemo(() => {
    const set = new Set(items.map((p) => yearOf(p.handover)).filter(Boolean));
    return [...set].sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (status) {
        // "Completed" chip matches both `ready` and legacy `completed` values.
        if (status === "ready" && !["ready", "completed"].includes(p.status)) return false;
        if (status !== "ready" && p.status !== status) return false;
      }
      if (emirate && (p.emirate || "Dubai") !== emirate) return false;
      if (community && p.community !== community) return false;
      if (developerId && p.developer_id !== developerId) return false;
      if (handoverYear && yearOf(p.handover) !== Number(handoverYear)) return false;
      if (priceMax && Number(p.starting_price || 0) > Number(priceMax)) return false;
      if (q) {
        const hay = `${p.name} ${p.community} ${p.developer_slug || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, status, emirate, community, developerId, handoverYear, priceMax, q]);

  const developerName = (id) => developers.find((d) => d.id === id)?.name;

  function clearAll() {
    setStatus(""); setEmirate(""); setCommunity(""); setDeveloperId("");
    setHandoverYear(""); setPriceMax(""); setQ("");
  }

  const activePills = [];
  if (status) activePills.push({ k: "status", label: status, clear: () => setStatus("") });
  if (emirate) activePills.push({ k: "emirate", label: emirate, clear: () => setEmirate("") });
  if (community) activePills.push({ k: "community", label: community, clear: () => setCommunity("") });
  if (developerId) activePills.push({ k: "developer", label: developerName(developerId) || "Developer", clear: () => setDeveloperId("") });
  if (handoverYear) activePills.push({ k: "handover", label: `Handover ${handoverYear}`, clear: () => setHandoverYear("") });
  if (priceMax) activePills.push({ k: "price", label: `Max AED ${Number(priceMax).toLocaleString()}`, clear: () => setPriceMax("") });
  if (q) activePills.push({ k: "q", label: `“${q}”`, clear: () => setQ("") });

  return (
    <div data-testid="projects-page">
      <SEO title="Off-plan projects in Dubai & Abu Dhabi" description="Curated off-plan and completed projects from Dubai's leading developers — Emaar, Nakheel, Meraas, Damac, Sobha, Select Group, Meydan, Aldar." path="/projects" />

      <section className="pt-6 pb-8 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Off-plan", path: "/projects" }]} />
          <div className="overline text-[#001588] mb-4">Off-plan · {items.length}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl mb-8">
            Off-plan.
          </h1>

          {/* Status tab chips — 3 sub-categories only */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="projects-status-chips">
            {[["off-plan", "New listing"], ["under-construction", "Under construction"], ["ready", "Completed"]].map(([k, label]) => (
              <button key={k} onClick={() => setStatus(status === k ? "" : k)} data-testid={`projects-filter-${k}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
                  status === k ? "bg-[#001588] text-white border border-[#001588]" : "border border-neutral-300 text-neutral-700 hover:border-[#001588]"
                }`}>
                {label}
              </button>
            ))}
          </div>

          {/* Filter panel */}
          <div className="bg-white border border-neutral-200 p-4 lg:p-5 shadow-sm" data-testid="projects-filter-panel">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-6 flex items-center bg-[#ffffff] border border-transparent focus-within:border-[#001588]">
                <MagnifyingGlass size={18} className="mx-4 text-neutral-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search project, community or developer…"
                       className="flex-1 bg-transparent py-3 text-[#050d19] placeholder:text-neutral-500 focus:outline-none pr-4"
                       data-testid="projects-search" />
              </div>
              <F label="Emirate">
                <select value={emirate} onChange={(e) => setEmirate(e.target.value)} className={inputCls} data-testid="projects-emirate">
                  <option value="">All emirates</option>
                  {EMIRATES.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </F>
              <F label="Community">
                <select value={community} onChange={(e) => setCommunity(e.target.value)} className={inputCls} data-testid="projects-community">
                  <option value="">All communities ({communityOptions.length})</option>
                  {communityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </F>
              <F label="Developer">
                <select value={developerId} onChange={(e) => setDeveloperId(e.target.value)} className={inputCls} data-testid="projects-developer">
                  <option value="">All developers</option>
                  {developers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </F>
              <F label="Handover year">
                <select value={handoverYear} onChange={(e) => setHandoverYear(e.target.value)} className={inputCls} data-testid="projects-handover">
                  <option value="">Any year</option>
                  {handoverYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </F>
              <F label="Max price (AED)">
                <input value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="e.g. 10,000,000" type="number" min="0" max="500000000" step="100000" className={inputCls} data-testid="projects-price-max" />
              </F>
              <F label="Reset">
                <button onClick={clearAll} className="w-full border border-neutral-300 text-neutral-700 h-[40px] text-xs uppercase tracking-[0.18em] hover:border-red-500 hover:text-red-600" data-testid="projects-clear">Clear all</button>
              </F>
            </div>

            {/* Active pills */}
            {activePills.length > 0 && (
              <div className="mt-4 pt-3 border-t border-neutral-100 flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mr-1">Active:</span>
                {activePills.map((p) => (
                  <button key={p.k} onClick={p.clear} className="inline-flex items-center gap-1.5 bg-[#001588] text-white text-xs px-3 py-1 hover:bg-black">
                    {p.label} <X size={10} weight="bold" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-sm text-neutral-500 mb-8" data-testid="projects-count">
            {loading ? "Loading…" : `${filtered.length} project${filtered.length === 1 ? "" : "s"} matching`}
          </div>

          {!loading && filtered.length === 0 ? (
            <div className="border border-neutral-200 p-14 text-center text-neutral-500">
              No matching projects. Try adjusting your filters or <Link to="/contact" className="text-[#001588] underline">enquire with an advisor</Link>.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((p) => (
                <Link key={p.slug} to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="group block border border-neutral-200 hover:border-[#001588] transition-colors">
                  <div className="pp-hover-zoom aspect-[4/3] relative">
                    <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                    {p.featured && <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.18em] bg-[#ffffff] text-black px-2 py-1">Featured</span>}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between overline text-[#001588] mb-2">
                      <span>{p.status}</span>
                      {p.handover && <span className="text-neutral-500">Handover {p.handover}</span>}
                    </div>
                    <h2 className="font-serif text-2xl group-hover:text-[#001588] line-clamp-1">{p.name}</h2>
                    <div className="text-xs text-neutral-500 mt-2">{p.community}{p.emirate && p.emirate !== "Dubai" ? ` · ${p.emirate}` : ""}</div>
                    <div className="mt-4 pt-4 border-t border-neutral-200 flex items-center justify-between">
                      <span className="font-serif text-lg text-neutral-900">{p.starting_price ? `from AED ${Number(p.starting_price).toLocaleString()}` : "Price on request"}</span>
                      <CaretRight size={16} className="text-neutral-400 group-hover:text-[#001588]" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-white border border-neutral-200 h-[40px] px-3 text-sm text-[#050d19] focus:outline-none focus:border-[#001588] cursor-pointer appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]";

function F({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
